import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { KnowledgeBase } from '@/hooks/useKnowledgeBases'
import { LLMConfig } from './Step3LLMConfig'
import { PromptConfig } from './Step4PromptConfig'

interface Step5ReviewProps {
  name: string
  description: string
  selectedKBs: KnowledgeBase[]
  llmConfig: LLMConfig
  promptConfig: PromptConfig
}

export function Step5Review({
  name,
  description,
  selectedKBs,
  llmConfig,
  promptConfig,
}: Step5ReviewProps) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-1">Review Configuration</h3>
        <p className="text-sm text-muted-foreground">
          Review your assistant configuration before creating
        </p>
      </div>

      {/* Basic Information */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Basic Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Name</span>
            <span className="font-medium">{name}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Description</span>
            <span className="font-medium text-right max-w-[60%] truncate">
              {description || 'No description'}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Knowledge Bases */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Knowledge Bases</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="text-muted-foreground mb-2">
            {selectedKBs.length} knowledge base{selectedKBs.length !== 1 ? 's' : ''} selected
          </div>
          <ul className="list-disc list-inside space-y-1">
            {selectedKBs.map((kb) => (
              <li key={kb.id} className="truncate">
                {kb.name}
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {/* LLM Settings */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">LLM Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Model</span>
            <span className="font-medium">Managed by ChatterPillar</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Temperature</span>
            <span className="font-medium">{llmConfig.temperature.toFixed(1)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Top P</span>
            <span className="font-medium">{llmConfig.topP.toFixed(1)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Presence Penalty</span>
            <span className="font-medium">{llmConfig.presencePenalty.toFixed(1)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Frequency Penalty</span>
            <span className="font-medium">{llmConfig.frequencyPenalty.toFixed(1)}</span>
          </div>
        </CardContent>
      </Card>

      {/* Prompt & Retrieval Settings */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Prompt & Retrieval Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Opening Message</span>
            <span className="font-medium text-right max-w-[60%] truncate">
              {promptConfig.opener}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Empty Response</span>
            <span className="font-medium text-right max-w-[60%] truncate">
              {promptConfig.emptyResponse}
            </span>
          </div>
          <div>
            <span className="text-muted-foreground">System Prompt</span>
            <p className="mt-1 text-xs bg-muted p-2 rounded font-mono whitespace-pre-wrap">
              {promptConfig.systemPrompt.length > 200
                ? `${promptConfig.systemPrompt.slice(0, 200)}...`
                : promptConfig.systemPrompt}
            </p>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Similarity Threshold</span>
            <span className="font-medium">{promptConfig.similarityThreshold.toFixed(2)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Keywords Weight</span>
            <span className="font-medium">{promptConfig.keywordsSimilarityWeight.toFixed(2)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Top N Chunks</span>
            <span className="font-medium">{promptConfig.topN}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Show Quote</span>
            <span className="font-medium">{promptConfig.showQuote ? 'Yes' : 'No'}</span>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
